/**
 * @fileoverview Section "Spacing" — grille 4 px de Tailwind + exemples GNPC.
 *
 * Illustre l'échelle standard des utilitaires (0.5 → 24) avec leurs valeurs
 * en rem et pixels, plus les patterns d'espacement les plus courants pour
 * les formulaires, listes et cartes.
 */

import { CopyButton } from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

const SCALE = [
  { token: '0.5', rem: '0.125rem', px: 2 },
  { token: '1', rem: '0.25rem', px: 4 },
  { token: '2', rem: '0.5rem', px: 8 },
  { token: '3', rem: '0.75rem', px: 12 },
  { token: '4', rem: '1rem', px: 16 },
  { token: '6', rem: '1.5rem', px: 24 },
  { token: '8', rem: '2rem', px: 32 },
  { token: '12', rem: '3rem', px: 48 },
  { token: '16', rem: '4rem', px: 64 },
  { token: '24', rem: '6rem', px: 96 },
] as const;

export function DsSectionSpacing(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Échelle</h3>
        <p className="text-xs text-muted-foreground">
          Base 4 px. Utiliser les incréments standards ; éviter les valeurs arbitraires
          <code> [17px]</code> — casse la cohérence rythmique.
        </p>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Token</th>
                <th className="px-4 py-2 text-left font-medium">Valeur</th>
                <th className="px-4 py-2 text-left font-medium">Aperçu</th>
                <th className="px-4 py-2 text-left font-medium">Copie</th>
              </tr>
            </thead>
            <tbody>
              {SCALE.map((s) => (
                <tr key={s.token} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{s.token}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground tabular-nums">
                    {s.rem} · {s.px} px
                  </td>
                  <td className="px-4 py-2">
                    <div
                      className="h-2 rounded-sm bg-quipux-orange-500"
                      style={{ width: `${s.px}px` }}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <CopyButton value={`p-${s.token}`} ariaLabel={`Copier p-${s.token}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Patterns récurrents</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label="gap-2 — badges alignés">
            <div className="flex gap-2">
              <span className="rounded-md bg-quipux-navy-100 px-2 py-1 text-xs">A</span>
              <span className="rounded-md bg-quipux-navy-100 px-2 py-1 text-xs">B</span>
              <span className="rounded-md bg-quipux-navy-100 px-2 py-1 text-xs">C</span>
            </div>
          </DsPreview>
          <DsPreview label="gap-4 — items de liste">
            <div className="flex flex-col gap-4">
              <div className="rounded-md bg-quipux-navy-50 p-3 text-xs">Ligne 1</div>
              <div className="rounded-md bg-quipux-navy-50 p-3 text-xs">Ligne 2</div>
              <div className="rounded-md bg-quipux-navy-50 p-3 text-xs">Ligne 3</div>
            </div>
          </DsPreview>
          <DsPreview label="p-6 — carte standard">
            <div className="rounded-md border border-border bg-card p-6 text-xs text-muted-foreground">
              Contenu de carte
            </div>
          </DsPreview>
        </div>
      </div>
    </div>
  );
}
